import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase } from './supabase'
import type { AppUser } from './types'

/**
 * Four states, and the distinction between the last two is the whole point of
 * this file.
 *
 *   loading    — we do not yet know
 *   signed-out — no Google session
 *   denied     — a VALID Google session belonging to somebody who is not on the
 *                app_users allowlist. Google OAuth authenticates any Google
 *                account in existence; a row in app_users is what makes someone
 *                a user of this application.
 *   ready      — signed in and allowlisted
 *
 * RLS already guarantees a denied user sees zero rows (verified against the
 * live API on 2026-08-13: HTTP 200, empty array, on every table). But a
 * stranger reaching a logged-in shell — even an empty one — is not acceptable,
 * so we check at the door and sign them straight back out.
 */
export type AuthStatus = 'loading' | 'signed-out' | 'denied' | 'ready'

type AuthValue = {
  status: AuthStatus
  session: Session | null
  appUser: AppUser | null
  /** Email of the account that was refused, for the message on the denied screen. */
  deniedEmail: string | null
  /**
   * Why the user is looking at the sign-in page, when it is not simply that
   * they arrived signed out. Shown on the login card.
   */
  notice: string | null
  signIn: () => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading')
  const [session, setSession] = useState<Session | null>(null)
  const [appUser, setAppUser] = useState<AppUser | null>(null)
  const [deniedEmail, setDeniedEmail] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  /**
   * Set while we are signing somebody out *because* they were refused.
   *
   * Refusing a user means doing two things — showing "no access", and killing
   * the session — and the second one destroys the first if you let it. signOut
   * fires onAuthStateChange with a null session, which resolves to
   * 'signed-out' and wipes the 'denied' screen milliseconds after it renders.
   * The user gets bounced back to the login page with no explanation, tries
   * again, and loops forever with no idea why.
   *
   * Found by testing the refusal path against a real account rather than
   * assuming it worked. Access was correctly denied throughout — but a silent
   * loop is its own failure, and it is exactly what Renee would hit if her
   * allowlist row were ever misconfigured.
   */
  const denying = useRef(false)

  useEffect(() => {
    let cancelled = false

    /**
     * current_app_user() is a security-definer function that reads app_users by
     * auth.uid(). Using it rather than a plain select means the allowlist check
     * runs through exactly the same code path the RLS policies use, so the two
     * cannot drift apart.
     */
    async function resolve(next: Session | null) {
      if (cancelled) return

      setSession(next)

      if (!next) {
        setAppUser(null)
        /**
         * A sign-out we triggered ourselves to enforce a refusal must not
         * overwrite the refusal message with the plain login page.
         *
         * The flag is deliberately NOT cleared here. Supabase can emit more
         * than one null-session event — a failed token refresh followed by the
         * sign-out, for instance — and clearing on the first one let the second
         * fall through to 'signed-out', wiping the refusal a few hundred
         * milliseconds after it rendered. That is the same silent-loop failure
         * B3 documented, arriving by a different route. It is cleared only on a
         * deliberate act by the user: signIn or an explicit signOut.
         */
        if (denying.current) {
          setStatus('denied')
          return
        }
        setStatus('signed-out')
        return
      }

      const { data, error } = await supabase.rpc('current_app_user')
      if (cancelled) return

      if (error) {
        /**
         * Fail closed — an unreadable allowlist is not permission to proceed.
         *
         * But do NOT send them to the "No access" screen. This branch fires
         * whenever the *check itself* could not run, and by far the commonest
         * cause is an expired session: the token dies, PostgREST falls back to
         * the `anon` role, and `anon` has deliberately been granted nothing, so
         * the call comes back "permission denied for table agents".
         *
         * Chris hit exactly this on 2026-08-14 and reasonably concluded the
         * database had lost its permissions. It had not. The grants were
         * perfect; the session was dead. Telling Renee "No access" would send
         * her to Chris with the same wrong conclusion.
         *
         * So: end the session and put her back on the sign-in page with a
         * sentence that names the actual remedy.
         */
        console.error('Allowlist check failed:', error)
        setDeniedEmail(null)
        setAppUser(null)
        setNotice('Your sign-in expired. Please sign in again.')
        setStatus('signed-out')
        await supabase.auth.signOut()
        return
      }

      const row = (Array.isArray(data) ? data[0] : data) as AppUser | null

      /**
       * Do not test this with a truthiness check on `row`.
       *
       * current_app_user() returns the app_users composite type. When there is
       * no matching row, Postgres returns a row of NULLs rather than nothing,
       * and PostgREST faithfully renders that as a complete JSON object:
       *
       *   {"id":null,"email":null,"role":null,"is_active":null, ...}
       *
       * which is truthy. Confirmed against the live API on 2026-08-13. `id` is
       * the primary key, so its absence is the unambiguous signal that no
       * allowlist row exists.
       */
      if (!row?.id || !row.is_active) {
        // A genuine refusal: the check ran and the answer was no.
        setNotice(null)
        setDeniedEmail(next.user.email ?? null)
        setAppUser(null)
        denying.current = true
        setStatus('denied')
        // Do not leave a valid session sitting in the browser for an account
        // that has no business holding one.
        await supabase.auth.signOut()
        return
      }

      setAppUser(row)
      setDeniedEmail(null)
      setNotice(null)
      setStatus('ready')
    }

    /**
     * `resolve` decides which of four screens the whole application shows, and
     * until this wrapper existed it could throw and decide nothing.
     *
     * THE STATE THIS PREVENTS: "Checking access…", forever.
     *
     * Every *returned* error inside `resolve` is handled carefully — the
     * expired-session branch above is three paragraphs of hard-won reasoning.
     * But a `throw` was handled nowhere. `getSession()` and `signOut()` both
     * reach the browser's lock and storage APIs and can reject rather than
     * return; `resolve` is called with `void`, so a rejection went nowhere and
     * `status` stayed `'loading'`. The gate card renders on `'loading'`, so the
     * app sits on "Checking access…" with no error, no button, and nothing on
     * screen suggesting a reload would help.
     *
     * Fail to the sign-in page, which is the one screen that is always safe to
     * show and always has a way forward.
     */
    const attempt = (next: Session | null) =>
      resolve(next).catch((e) => {
        if (cancelled) return
        console.error('Sign-in check failed:', e)
        setAppUser(null)
        setDeniedEmail(null)
        setNotice('Something went wrong while checking your sign-in. Please sign in again.')
        setStatus('signed-out')
      })

    supabase.auth
      .getSession()
      .then(({ data }) => attempt(data.session))
      /**
       * `getSession()` itself can reject, and that rejection used to mean the
       * `.then` never ran at all — so `resolve` was never called, and nothing
       * ever moved `status` off `'loading'`.
       */
      .catch((e) => {
        if (cancelled) return
        console.error('Could not read the stored session:', e)
        setNotice('Something went wrong while checking your sign-in. Please sign in again.')
        setStatus('signed-out')
      })

    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      // Never leave the app in a stale 'ready' state while re-checking.
      void attempt(next ?? null)
    })

    return () => {
      cancelled = true
      sub.subscription.unsubscribe()
    }
  }, [])

  const value = useMemo<AuthValue>(
    () => ({
      status,
      session,
      appUser,
      deniedEmail,
      notice,
      signIn: async () => {
        denying.current = false
        setNotice(null)
        await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: window.location.origin,
            queryParams: { prompt: 'select_account' },
          },
        })
      },
      signOut: async () => {
        // An explicit sign-out, including "try a different account" from the
        // denied screen, really does mean go back to the login page.
        denying.current = false
        setDeniedEmail(null)
        setNotice(null)
        await supabase.auth.signOut()
        setStatus('signed-out')
      },
    }),
    [status, session, appUser, deniedEmail, notice],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
